import type { FaireReviewEntity } from "@faire/playground-entity-db/entity_types/review";
import { getAllEntities, getEntityByToken } from "../get_entity";

const REVIEW_TOKENS = [
  "review_r1",
  "review_r2",
  "review_r3",
  "review_r4",
  "review_r5",
  "review_r6",
  "review_r7",
  "review_r8",
];

const reviewEntities = REVIEW_TOKENS.map((token) =>
  getEntityByToken(token, "review")
).filter(
  (review: FaireReviewEntity | undefined): review is FaireReviewEntity =>
    review !== undefined
);

export const getReviews = (): FaireReviewEntity[] => reviewEntities;

/** Every real 5-star review of a specific product — distinct from
 * `getReviews()`'s flat brand-agnostic mock list above. Empty when none is
 * imported yet for this product; callers should omit the quote, not fabricate
 * one or borrow another product's.
 *
 * Returns the whole set rather than one review because which of them belongs on
 * a brand's home page is an editorial judgement, not a data one — see
 * `reviewQuality.ts` in the brand-home framework (Ryan, 2026-09-17). */
export const getReviewsForProduct = (
  productToken: string
): FaireReviewEntity[] =>
  getAllEntities("review").filter(
    (review) =>
      review.reviewType === "product" &&
      review.productToken === productToken &&
      review.stars === 5
  );

/** @deprecated Picks whichever 5-star review happens to be stored first, which
 * is how seven-line paragraphs and colour-mismatched quotes reached the page.
 * Use `getReviewsForProduct` with `pickBestReview` instead. */
export const getReviewForProduct = (
  productToken: string
): FaireReviewEntity | undefined => getReviewsForProduct(productToken)[0];

/** Real brand-level reviews (not tied to a single product) for a brand's
 * reviews carousel — e.g. a shopper's overall review of the brand, with the
 * multiple products they purchased. Newest first, rather than entity-db's
 * incidental token-sort order. */
export const getBrandReviews = (brandToken: string): FaireReviewEntity[] =>
  getAllEntities("review")
    .filter(
      (review) =>
        review.reviewType === "brand" && review.brandToken === brandToken
    )
    .sort(
      (left, right) =>
        new Date(right.reviewDate).getTime() -
        new Date(left.reviewDate).getTime()
    );
